import type { CloseoutLitresCheck, OdoDayCheck, OdoFactorSuggestion, FillPlan, FillVariance, FirstFillGate, FuelKpiStatus, FuelLevel, FuelLogStatus, FuelVerification, HandoverKind, PrevFillEfficiency, VehicleExpenseCategory } from '@mt/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { prepareReportPhoto } from '@/lib/report-photo';
import { FLEET_KEY } from './api';

/** P12 B2 (docs/phases/P12.md §9): nhận/trả xe, phiếu dầu, chốt kỳ, báo cáo dầu, khoán dầu. */
export interface Handover {
  id: number;
  vehicleId: number;
  plateNo: string | null;
  driverId: number;
  driverName: string | null;
  kind: HandoverKind;
  odometerKm: number;
  fuelLevelPct: FuelLevel;
  odometerPhotoId: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  occurredAt: string;
}

export interface FuelLog {
  id: number;
  vehicleId: number;
  plateNo: string | null;
  driverId: number | null;
  driverName: string | null;
  filledAt: string;
  periodKey: string;
  odometerKm: number;
  litres: number;
  unitPrice: number;
  amount: number;
  station: string | null;
  fullTank: boolean;
  receiptPhotoId: number | null;
  odometerPhotoId: number | null;
  stationPhotoId: number | null;
  stationId: number | null;
  stationName: string | null;
  stationConfirmed: boolean | null;
  driverAsiaCode: string | null;
  verification: FuelVerification | null;
  autoApproved: boolean;
  ocrLitres: number | null;
  ocrAmount: number | null;
  ocrOdometerKm: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  status: FuelLogStatus;
  decidedByName: string | null;
  decidedAt: string | null;
  rejectReason: string | null;
  isManual: boolean;
  manualReason: string | null;
  plan: FillPlan | null;
  /** P12 PR6: chênh lệch tại lần đổ này (km ODO vs GPS, lít so định mức) – cảnh báo ngay, không dồn cuối tháng */
  variance: FillVariance | null;
  varianceFlag: boolean;
  /** DEC-290: mốc GPS lúc đổ, km chặng theo đồng hồ / GPS, tiêu hao 2 cách (chỉ khi đổ đầy bình) */
  gpsMarkAt: string | null;
  gpsDayKm: number | null;
  gpsKmSincePrev: number | null;
  /** Chỉ ở chi tiết phiếu (quản lý): ngày trong chặng không có số GPS → km định vị để trống (21/10/2026) */
  gpsMissingDays?: string[];
  odoKmSincePrev: number | null;
  /** DEC-661: km từ lần đổ trước (đi qua chốt kỳ xen giữa) – dùng cho hiệu suất, chi tiết tính toán */
  odoKmSinceFill?: number | null;
  gpsKmSinceFill?: number | null;
  /** Lũy kế từ chốt đầu tháng (chốt kỳ trước) tới lần đổ này; chưa có chốt đầu tháng thì null */
  openingMarkAt: string | null;
  openingOdometerKm: number | null;
  odoKmSinceOpening: number | null;
  gpsKmSinceOpening: number | null;
  /** ODO − GPS (km) và % trên km ODO */
  kmDiffSinceOpening: number | null;
  kmDiffPctSinceOpening: number | null;
  usedLitres: number | null;
  odoLPer100km: number | null;
  odoKmPerL: number | null;
  gpsLPer100km: number | null;
  gpsKmPerL: number | null;
  /** P12 §16 (DEC-291): phút cẩu trong chặng + lít dầu cẩu đã trừ trước khi tính tiêu hao (lít cẩu chỉ quản lý thấy) */
  craneMinutes: number | null;
  craneLitres: number | null;
  /** Nguyên nhân tài xế ghi khi bị cảnh báo hao dầu */
  driverCause: string | null;
  driverCauseAt: string | null;
  /** Tính cho lần đổ trước (19/10/2026): lần đổ / chốt kỳ trước, hiệu suất theo lít lần trước, cờ dưới mức + bắt giải trình */
  previous: { source: 'fuel' | 'closeout'; id: number; litres: number; odometerKm: number; at: string } | null;
  prevEfficiency: PrevFillEfficiency | null;
  underNorm: boolean;
  explainRequired: boolean;
  /** DEC-562: sai số đồng hồ ODO của xe (km ODO ÷ km GPS lúc bình thường), chỉ giám đốc nhận được; null = chưa ghi nhận */
  odoFactor?: number | null;
  version: number;
  createdAt: string;
}

/** Chốt kỳ (P12 PR6: vị trí, số AI đọc ODO, đối chiếu lít còn) */
export interface Closeout {
  id: number;
  periodKey: string;
  vehicleId: number;
  litresLeft: number;
  odometerKm: number;
  lat: number | null;
  lng: number | null;
  ocrOdometerKm: number | null;
  litresCheck: CloseoutLitresCheck | null;
  litresFlag: boolean;
  /** DEC-290: mốc GPS lúc chốt + km chặng từ lần đổ / chốt kỳ trước */
  gpsMarkAt?: string | null;
  gpsKmSincePrev?: number | null;
  odoKmSincePrev?: number | null;
  /** Cả kỳ: từ chốt kỳ trước (đầu tháng) tới chốt kỳ này */
  openingMarkAt?: string | null;
  odoKmSinceOpening?: number | null;
  gpsKmSinceOpening?: number | null;
  kmDiffSinceOpening?: number | null;
  kmDiffPctSinceOpening?: number | null;
}

export interface CloseoutRequirement {
  required: boolean;
  periodKey: string;
  deadlineAt: string;
}

/** X-PWA bước 4: km từ lần đổ (hoặc chốt kỳ) gần nhất của xe */
export interface SinceFill {
  source: 'fuel' | 'closeout';
  at: string;
  litres: number;
  odometerKm: number;
  drivenKm: number | null;
  gpsKm: number | null;
}

export interface DriverHome {
  driver: { id: number; fullName: string } | null;
  /** home: điểm xuất phát lộ trình (điểm về riêng của xe, chưa gán thì bãi xe chung) */
  vehicle: { id: number; plateNo: string; role: string; tankLitres: number | null; odometerKm: number | null; home: { name: string; lat: number; lng: number } | null } | null;
  openReceive: Handover | null;
  closeout: CloseoutRequirement | null;
  lastFuel: FuelLog | null;
  fuelLogs: FuelLog[];
  handovers: Handover[];
  period: { key: string; filledLitres: number; fillCount: number } | null;
  /** Mốc kỳ dầu dạng "07:00" (P12 PR6, DEC-210) */
  periodAnchor: string | null;
  sinceFill: SinceFill | null;
}

export interface FuelReportRow {
  vehicleId: number;
  plateNo: string;
  norm: number | null;
  driver: { id: number; fullName: string; asiaStaffCode: string | null } | null;
  driverChanged: boolean;
  opening: { litresLeft: number; odometerKm: number } | null;
  closing: { litresLeft: number; odometerKm: number; id: number; version: number } | null;
  filledLitres: number;
  filledAmount: number;
  fillCount: number;
  pendingCount: number;
  needMoreKmCount: number;
  verify: { total: number; ok: number; warn: number; unverified: number; autoApproved: number };
  avgPrice: number | null;
  odoKm: number | null;
  gpsKm: number | null;
  gpsDays: number;
  /** Kỳ có ngày thiếu GPS: km GPS = đo được + bù ODO ÷ sai số đồng hồ; missingDays = ngày không bù được */
  gpsEstimate: { measuredKm: number; estimatedKm: number; estimatedDays: string[]; missingDays: string[] } | null;
  /** Km GPS theo mốc giờ chốt đầu tháng → chốt cuối tháng (cộng các chặng lần đổ) */
  gpsKmByMarks: number | null;
  litresUsed: number | null;
  actualPer100: number | null;
  kpiStatus: FuelKpiStatus;
  mismatch: boolean;
  mismatchLimitKm: number | null;
  /** Dầu cẩu cộng vào lít khoán (DEC-291 bổ sung), chỉ màn quản lý; text = "Dầu cẩu: X giờ × Y L/giờ = Z L" */
  crane: { sessions: number; minutes: number; lPerHour: number | null; litres: number | null; text: string | null } | null;
  /** DEC-482: lần đổ cuối tháng trước / lần đổ đầu tháng này + đối chiếu tối thiểu % kế hoạch */
  lastPrevFill: { litres: number; odometerKm: number; at: string } | null;
  firstFill: { id: number; litres: number; odometerKm: number; at: string; isManual: boolean; gate: FirstFillGate | null } | null;
  settlement: {
    kmBasis: number | null;
    kmSource: 'override' | 'gps' | 'odo' | 'none';
    mismatch: boolean;
    litresAllowed: number | null;
    craneLitres: number | null;
    litresDiff: number | null;
    amount: number | null;
    missing: string[];
    id: number | null;
    status: 'none' | 'draft' | 'approved';
    kmOverride: number | null;
    overrideReason: string | null;
    approvedByName: string | null;
    approvedAt: string | null;
    version: number | null;
  };
}

export interface FuelReport {
  periodKey: string;
  startAt: string;
  endAt: string;
  rows: FuelReportRow[];
  totals: FuelSummary;
}

/** Tổng hợp cả đội (summarizeFuelRows ở API) */
export interface FuelSummary {
  vehicles: number;
  filledLitres: number;
  filledAmount: number;
  fillCount: number;
  avgPrice: number | null;
  litresUsed: number | null;
  closedCount: number;
  openingLitres: number;
  closingLitres: number;
  kmBasis: number;
  odoKm: number;
  gpsKm: number;
  per100: number | null;
  costPerKm: number | null;
  bonus: number;
  penalty: number;
  net: number;
  pending: number;
  mismatch: number;
  overCount: number;
  savingCount: number;
  approvedCount: number;
  readyCount: number;
  noAsiaCode: number;
  verifyTotal: number;
  verifyOk: number;
  verifyWarn: number;
}

export interface FuelTrendPeriod extends FuelSummary {
  periodKey: string;
  byVehicle: {
    vehicleId: number;
    plateNo: string;
    norm: number | null;
    per100: number | null;
    litresUsed: number | null;
    kmBasis: number | null;
    filledLitres: number;
    filledAmount: number;
    amount: number | null;
  }[];
}

export interface KmDaily {
  vehicleId: number;
  plateNo: string;
  norm: number | null;
  periodKey: string;
  days: { day: string; gpsKm: number | null; source: 'gps_binhminh' | 'manual' | null; litres: number; fillCount: number }[];
  totals: { gpsKm: number; gpsDays: number; manualDays: number; litres: number };
}

export interface FuelStation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  radiusM: number;
  isActive: boolean;
  confirmed: boolean;
  confirmedByName: string | null;
  confirmedAt: string | null;
  fillCount: number;
  version: number;
  createdAt: string;
}

/** Màn tài xế → Tháng của tôi (anh Tiến 20/10/2026): dầu, chi phí xe, lương của một kỳ. Không có định mức / khoán. */
export interface DriverMonth {
  periodKey: string;
  periods: string[];
  vehicles: { id: number; plateNo: string }[];
  fuel: { count: number; litres: number; amount: number; avgPrice: number | null; odoKm: number; gpsKm: number };
  fills: FuelLog[];
  /** Anh Tiến 01/11/2026: dầu còn trong bình lúc chốt kỳ trước mang sang đầu tháng này (chỉ xem, không cộng tiền dầu tháng này) */
  opening: {
    fromPeriod: string;
    litres: number;
    amount: number | null;
    vehicles: { vehicleId: number; plateNo: string; litres: number; odometerKm: number; unitPrice: number | null; amount: number | null }[];
  } | null;
  deliveries: { notes: number; orders: number; items: { id: number; code: string; orderCode: string; customerName: string; address: string | null; deliveredAt: string }[] };
  expenses: {
    id: number;
    vehicleId: number;
    plateNo: string;
    category: VehicleExpenseCategory;
    status: 'pending' | 'approved';
    amount: number;
    occurredAt: string;
    vendor: string | null;
    tirePosition: string | null;
    note: string | null;
  }[];
  expenseTotal: number;
  payslip: { id: number; gross: number; net: number; workDays: number; stdDays: number } | null;
  costTotal: number;
}
export const useDriverMonth = (period: string | null) =>
  useQuery({ queryKey: [...FLEET_KEY, 'me', 'month', period], queryFn: () => api.get<DriverMonth>(`/fleet/me/month${period ? `?period=${period}` : ''}`) });

export const useDriverHome = () => useQuery({ queryKey: [...FLEET_KEY, 'me'], queryFn: () => api.get<DriverHome>('/fleet/me') });
export const useFuelLogs = (f: { status?: FuelLogStatus | ''; vehicleId?: number | null; period?: string | null; flag?: 'warn' | null; q?: string }) => {
  const qs = new URLSearchParams();
  if (f.status) qs.set('status', f.status);
  if (f.vehicleId) qs.set('vehicleId', String(f.vehicleId));
  if (f.period) qs.set('period', f.period);
  if (f.flag) qs.set('flag', f.flag);
  if (f.q?.trim()) qs.set('q', f.q.trim());
  return useQuery({ queryKey: [...FLEET_KEY, 'fuel', f], queryFn: () => api.get<FuelLog[]>(`/fleet/fuel?${qs}`) });
};
export const useFuelLog = (id: number | null) =>
  useQuery({ queryKey: [...FLEET_KEY, 'fuel-log', id], queryFn: () => api.get<FuelLog>(`/fleet/fuel/${id}`), enabled: !!id });
export const useFuelReport = (period: string) =>
  useQuery({ queryKey: [...FLEET_KEY, 'fuel-report', period], queryFn: () => api.get<FuelReport>(`/fleet/fuel-report?period=${period}`) });
export const useFuelTrend = (to: string, months: number) =>
  useQuery({ queryKey: [...FLEET_KEY, 'fuel-trend', to, months], queryFn: () => api.get<{ periods: FuelTrendPeriod[] }>(`/fleet/fuel-report/trend?to=${to}&months=${months}`) });
export const useKmDaily = (vehicleId: number | null, period: string) =>
  useQuery({
    queryKey: [...FLEET_KEY, 'km-daily', vehicleId, period],
    queryFn: () => api.get<KmDaily>(`/fleet/vehicles/${vehicleId}/km-daily?period=${period}`),
    enabled: !!vehicleId,
  });
export const useFuelStations = () => useQuery({ queryKey: [...FLEET_KEY, 'stations'], queryFn: () => api.get<FuelStation[]>('/fleet/stations') });
export const useUserOptions = (enabled: boolean) =>
  useQuery({
    queryKey: [...FLEET_KEY, 'user-options'],
    queryFn: () => api.get<{ id: number; username: string; fullName: string }[]>('/fleet/user-options'),
    enabled,
    staleTime: 60_000,
  });

/** Vị trí điện thoại lúc chụp (không bắt buộc; từ chối quyền vị trí hoặc chờ quá 3 giây thì gửi không kèm vị trí). */
/** highAccuracy = false: lấy vị trí theo mạng (WiFi / trạm), dùng khi GPS không bắt được (trong xưởng mái tôn). */
export function currentPosition(waitMs = 3000, highAccuracy = true): Promise<{ lat: number; lng: number } | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), waitMs);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        resolve({ lat: Math.round(p.coords.latitude * 1e6) / 1e6, lng: Math.round(p.coords.longitude * 1e6) / 1e6 });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: highAccuracy, timeout: waitMs, maximumAge: 60_000 },
    );
  });
}

export type PhotoKind = 'odometer' | 'receipt' | 'fuel_gauge' | 'station' | 'invoice' | 'item' | 'other' | 'position';
export async function uploadFleetPhoto(kind: PhotoKind, file: File) {
  // Ảnh báo cáo 16:9 + dấu Ngày – Giờ – Vị trí (X-PWA §6)
  const { data, thumb, lat, lng, sourceSha256 } = await prepareReportPhoto(file);
  // Giờ chụp theo điện thoại (ảnh vừa chụp bằng camera: lastModified = lúc chụp) để server kiểm ảnh mới ≤ 10 phút (DEC-193)
  const takenAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
  return api.post<{ id: number }>('/fleet/photos', { kind, data, thumb, lat, lng, takenAt, sourceSha256 });
}

/** Ảnh cần token → tải blob rồi tạo object URL. */
export function useFleetPhotoSrc(id: number | null, size: 'thumb' | 'full' = 'thumb') {
  const q = useQuery({
    queryKey: ['fleet-photo', id, size],
    queryFn: () => api.blob(`/fleet/photos/${id}/file`, { size }),
    enabled: !!id,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    // Ô ảnh bị xóa / đổi ảnh: bỏ ảnh cũ, không để ảnh thu nhỏ cũ nằm lại trong ô trống
    if (!id || !q.data) {
      setSrc(null);
      return;
    }
    const u = URL.createObjectURL(q.data);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [id, q.data]);
  return src;
}

export const periodLabel = (key: string) => `${key.slice(5)}/${key.slice(0, 4)}`;
export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });

/** Khoảng kỳ dầu; mốc mặc định 07:00 (DEC-210, tham số trong DB – màn Hiệu quả xe). */
export const periodRange = (key: string, anchor = '07:00') => {
  const [y, m] = key.split('-').map(Number) as [number, number];
  const next = m === 12 ? `01/${y + 1}` : `${String(m + 1).padStart(2, '0')}/${y}`;
  return `${anchor} 01/${String(m).padStart(2, '0')} → ${anchor} 01/${next.slice(0, 2)}`;
};

/** DEC-562: sai số đồng hồ ODO từng xe (chỉ giám đốc: quyền duyệt dầu) */
export interface OdoFactorInfo {
  vehicleId: number;
  plateNo: string;
  factor: number | null;
  label: string;
  note: string | null;
  setAt: string | null;
  setByName: string | null;
  suggestion: OdoFactorSuggestion;
  days: { day: string; odoKm: number | null; gpsKm: number | null; ratio: number | null; check: OdoDayCheck }[];
}
export const useOdoFactor = (vehicleId: number | null) =>
  useQuery({
    queryKey: [...FLEET_KEY, 'odo-factor', vehicleId],
    queryFn: () => api.get<OdoFactorInfo>(`/fleet/vehicles/${vehicleId}/odo-factor`),
    enabled: !!vehicleId,
  });
